import { COLORS, GRID } from "features/chart";
import type { CandleWithFlag } from "features/chart";
import type { ServerBasePrice } from "services/types";

export const drawGrid = (
  ctx: CanvasRenderingContext2D,
  rect: DOMRect,
  maxPrice: number,
  minPrice: number,
  priceToY: (price: number) => number,
  visibleCount: number,
  candleWidth: number,
  start: number,
  dataArr: ServerBasePrice[] | CandleWithFlag[],
) => {
  const targetLines = 6;
  const w = rect.width;
  const h = rect.height;
  const left = GRID.left;
  const right = GRID.right;
  const top = GRID.top;
  const bottom = GRID.bottom;
  const chartHeight = h - top - bottom;
  const roughStep = (maxPrice - minPrice) / targetLines;
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const normalized = roughStep / magnitude;

  let stepMultiplier;
  if (normalized < 1.5) stepMultiplier = 1;
  else if (normalized < 2.5) stepMultiplier = 2;
  else if (normalized < 4) stepMultiplier = 2.5;
  else if (normalized < 7) stepMultiplier = 5;
  else stepMultiplier = 10;

  const priceStep = stepMultiplier * magnitude;
  const firstGridPrice = Math.ceil(minPrice / priceStep) * priceStep;

  const gridPrices: number[] = [];
  for (let price = firstGridPrice; price <= maxPrice; price += priceStep) {
    gridPrices.push(price);
    if (gridPrices.length > 10) break;
  }

  ctx.strokeStyle = COLORS.grid;
  ctx.lineWidth = 1;
  ctx.beginPath();

  gridPrices.forEach((price) => {
    const y = priceToY(price);
    if (y >= top && y <= top + chartHeight) {
      ctx.moveTo(left, y);
      ctx.lineTo(w - right, y);
    }
  });
  ctx.stroke();

  // Вертикальные линии
  ctx.strokeStyle = COLORS.grid;
  ctx.lineWidth = 1;

  const minVerticalSpacing = 30;
  let verticalLineStep = Math.max(
    1,
    Math.floor(minVerticalSpacing / candleWidth),
  );

  const maxVerticalLines = 20;
  if (visibleCount / verticalLineStep > maxVerticalLines) {
    verticalLineStep = Math.ceil(visibleCount / maxVerticalLines);
  }

  // Первый индекс данных в видимой области, выровненный по шагу сетки.
  // Это делает линии привязанными к конкретным свечам, а не к экранным пикселям.
  const firstAligned =
    Math.ceil(start / verticalLineStep) * verticalLineStep;

  ctx.beginPath();
  for (let idx = firstAligned; idx < start + visibleCount; idx += verticalLineStep) {
    const i = idx - start;
    const x = left + (i + 0.5) * candleWidth;
    ctx.moveTo(x, top);
    ctx.lineTo(x, h - bottom);
  }
  ctx.stroke();

  // Подписи цен
  ctx.fillStyle = COLORS.label;
  ctx.font = "10px Arial";
  ctx.textAlign = "right";
  ctx.textBaseline = "middle";

  gridPrices.forEach((price, index) => {
    const y = priceToY(price);
    if (y >= top && y <= top + chartHeight) {
      if (gridPrices.length <= 8 || index % 2 === 0) {
        ctx.fillText(price.toFixed(5), w - right - 4, y);
      }
    }
  });

  // Подписи времени
  ctx.textAlign = "center";
  ctx.textBaseline = "top";
  ctx.fillStyle = COLORS.label;
  ctx.font = "10px Arial";

  const lastCandle = dataArr[dataArr.length - 1];
  const timeframeMs =
    dataArr.length > 1
      ? new Date(dataArr[1].timestamp).getTime() -
        new Date(dataArr[0].timestamp).getTime()
      : 1000;

  for (let idx = firstAligned; idx < start + visibleCount; idx += verticalLineStep) {
    const i = idx - start;
    const x = left + (i + 0.5) * candleWidth;

    let timestamp: number;

    if (idx < dataArr.length) {
      timestamp = new Date(dataArr[idx].timestamp).getTime();
      ctx.globalAlpha = 1;
    } else {
      const futureIndex = idx - (dataArr.length - 1);
      timestamp =
        new Date(lastCandle.timestamp).getTime() + futureIndex * timeframeMs;

      ctx.globalAlpha = 0.4;
    }

    const date = new Date(timestamp);

    const label = `${date.getHours().toString().padStart(2, "0")}:${date
      .getMinutes()
      .toString()
      .padStart(2, "0")}${
      date.getSeconds() !== 0
        ? ":" + date.getSeconds().toString().padStart(2, "0")
        : ""
    }`;

    ctx.fillText(label, x, h - bottom + 4);
  }

  ctx.globalAlpha = 1;
};
